import { ArrowLeft, Loader2, Star } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ImageUploader } from '@/components/forms/ImageUploader';
import { contractorService } from '@/services/contractor.service';
import type { CreateContractorInput, UpdateContractorInput } from '@/types/contractor';
import { EntityStatus } from '@/types/contractor';

interface FormData {
  name: string;
  companyName: string;
  email: string;
  phone: string;
  profileLogo: string;
  rating: number;
  websiteUrl: string;
  description: string;
  insuranceDocument: string;
  licenseDocument: string;
  status: EntityStatus;
}

// Track display URLs separately (full URLs from API for display)
interface DisplayUrls {
  profileLogo?: string;
  insuranceDocument?: string;
  licenseDocument?: string;
}

const initialFormData: FormData = {
  name: '',
  companyName: '',
  email: '',
  phone: '',
  profileLogo: '',
  rating: 0,
  websiteUrl: '',
  description: '',
  insuranceDocument: '',
  licenseDocument: '',
  status: EntityStatus.ACTIVE,
};

export function ContractorForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id && id !== 'new';

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [displayUrls, setDisplayUrls] = useState<DisplayUrls>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);

  const fetchContractor = useCallback(async () => {
    if (!id) return;

    setIsFetching(true);
    setError(null);

    try {
      const contractor = await contractorService.getById(id);
      // API returns full URLs for display, but we store keys
      // For existing data, URLs are returned - we'll store them for display
      // and extract keys when saving (the API handles this transformation)
      setFormData({
        name: contractor.name,
        companyName: contractor.companyName,
        email: contractor.email || '',
        phone: contractor.phone || '',
        profileLogo: contractor.profileLogo || '',
        rating: contractor.rating,
        websiteUrl: contractor.websiteUrl || '',
        description: contractor.description || '',
        insuranceDocument: contractor.insuranceDocument || '',
        licenseDocument: contractor.licenseDocument || '',
        status: contractor.status,
      });
      // Store the display URLs (full URLs from API)
      setDisplayUrls({
        profileLogo: contractor.profileLogo,
        insuranceDocument: contractor.insuranceDocument,
        licenseDocument: contractor.licenseDocument,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contractor');
    } finally {
      setIsFetching(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode) {
      fetchContractor();
    }
  }, [isEditMode, fetchContractor]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, [name]: value.replace(/\D/g, '') }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRatingChange = (newRating: number) => {
    setFormData((prev) => ({
      ...prev,
      rating: newRating,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (isEditMode && id) {
        // When editing, send null to clear fields or the value to update
        const updateInput: UpdateContractorInput = {
          name: formData.name,
          companyName: formData.companyName,
          email: formData.email,
          phone: formData.phone,
          rating: formData.rating,
          status: formData.status,
          profileLogo: formData.profileLogo || null,
          websiteUrl: formData.websiteUrl || null,
          description: formData.description || null,
          insuranceDocument: formData.insuranceDocument || null,
          licenseDocument: formData.licenseDocument || null,
        };
        await contractorService.update(id, updateInput);
      } else {
        // When creating, only include fields with values
        const createInput: CreateContractorInput = {
          name: formData.name,
          companyName: formData.companyName,
          email: formData.email,
          phone: formData.phone,
          rating: formData.rating,
          status: formData.status,
        };
        if (formData.profileLogo) createInput.profileLogo = formData.profileLogo;
        if (formData.websiteUrl) createInput.websiteUrl = formData.websiteUrl;
        if (formData.description) createInput.description = formData.description;
        if (formData.insuranceDocument) createInput.insuranceDocument = formData.insuranceDocument;
        if (formData.licenseDocument) createInput.licenseDocument = formData.licenseDocument;
        await contractorService.create(createInput);
      }

      navigate('/contractors');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save contractor');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center py-[64px]">
        <div className="h-[32px] w-[32px] animate-spin rounded-full border-2 border-[#055135] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-[32px]">
      {/* Header */}
      <div className="flex items-center gap-[16px]">
        <button
          type="button"
          onClick={() => navigate('/contractors')}
          className="flex h-[40px] w-[40px] items-center justify-center rounded-[8px] text-[#646464] transition-colors hover:bg-[#f5f5f5] hover:text-[#181818]"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-[24px] font-semibold text-[#181818]">
          {isEditMode ? 'Edit Contractor' : 'Add Contractor'}
        </h1>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-[8px] border border-[#fca5a5] bg-[#fef2f2] p-[16px] text-[14px] text-[#b91c1c]">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-[24px]">
        <div className="overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white">
          <div className="border-b border-[#e5e5e5] bg-[#f5f5f5] px-[24px] py-[16px]">
            <h2 className="text-[16px] font-semibold text-[#181818]">Basic Information</h2>
          </div>
          <div className="space-y-[20px] p-[24px]">
            {/* Name & Company Name */}
            <div className="grid grid-cols-2 gap-[20px]">
              <div>
                <label
                  htmlFor="name"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Name <span className="text-[#d4183d]">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label
                  htmlFor="companyName"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Company Name <span className="text-[#d4183d]">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="ABC Construction"
                />
              </div>
            </div>

            {/* Email & Phone (Internal Use Only) */}
            <div className="grid grid-cols-2 gap-[20px]">
              <div>
                <label
                  htmlFor="email"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Email Address <span className="text-[#d4183d]">*</span>
                  <span className="ml-[8px] text-[12px] font-normal text-[#8d8c8f]">
                    (Internal use only)
                  </span>
                </label>
                <input
                  type="email"
                  pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                  title="Please enter a valid email address"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="contractor@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Phone Number <span className="text-[#d4183d]">*</span>
                  <span className="ml-[8px] text-[12px] font-normal text-[#8d8c8f]">
                    (Internal use only)
                  </span>
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Website URL */}
            <div>
              <label
                htmlFor="websiteUrl"
                className="mb-[8px] block text-[14px] font-medium text-[#181818]"
              >
                Website URL
              </label>
              <input
                type="url"
                id="websiteUrl"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleChange}
                className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                placeholder="https://example.com"
              />
            </div>

            {/* Rating & Status */}
            <div className="grid grid-cols-2 gap-[20px]">
              <div>
                <fieldset className="border-0 p-0 m-0">
                  <legend className="mb-[8px] block text-[14px] font-medium text-[#181818]">
                    Rating
                  </legend>
                  <div className="flex items-center gap-[8px]">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingChange(star)}
                        className="p-[4px] transition-transform hover:scale-110"
                      >
                        <Star
                          size={24}
                          className={
                            star <= formData.rating
                              ? 'fill-[#fbbf24] text-[#fbbf24]'
                              : 'text-[#e5e5e5] hover:text-[#fbbf24]'
                          }
                        />
                      </button>
                    ))}
                    <span className="ml-[8px] text-[14px] text-[#646464]">
                      {formData.rating.toFixed(1)}
                    </span>
                  </div>
                </fieldset>
              </div>
              <div>
                <label
                  htmlFor="status"
                  className="mb-[8px] block text-[14px] font-medium text-[#181818]"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="h-[48px] w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] text-[14px] text-[#181818] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                >
                  <option value={EntityStatus.ACTIVE}>Active</option>
                  <option value={EntityStatus.INACTIVE}>Inactive</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="description"
                className="mb-[8px] block text-[14px] font-medium text-[#181818]"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-[8px] border border-[#e5e5e5] bg-white px-[16px] py-[12px] text-[14px] text-[#181818] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none focus:ring-2 focus:ring-[#055135]/20"
                placeholder="Enter company description..."
              />
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="overflow-hidden rounded-[12px] border border-[#e5e5e5] bg-white">
          <div className="border-b border-[#e5e5e5] bg-[#f5f5f5] px-[24px] py-[16px]">
            <h2 className="text-[16px] font-semibold text-[#181818]">Documents & Media</h2>
          </div>
          <div className="space-y-[20px] p-[24px]">
            <ImageUploader
              label="Profile Logo"
              value={formData.profileLogo || undefined}
              displayUrl={displayUrls.profileLogo}
              onChange={(key) => setFormData((prev) => ({ ...prev, profileLogo: key || '' }))}
              folder="contractors/logo"
              entityId={id}
              accept=".png,.jpg,.jpeg,.webp"
              validateImageType
              maxFileSize={2 * 1024 * 1024}
            />

            <div className="grid grid-cols-2 gap-[20px]">
              <ImageUploader
                label="Insurance Document"
                value={formData.insuranceDocument || undefined}
                displayUrl={displayUrls.insuranceDocument}
                onChange={(key) =>
                  setFormData((prev) => ({ ...prev, insuranceDocument: key || '' }))
                }
                folder="contractors/insurance"
                entityId={id}
                accept=".pdf,.doc,.docx,image/*"
                isDocument
              />
              <ImageUploader
                label="License Document"
                value={formData.licenseDocument || undefined}
                displayUrl={displayUrls.licenseDocument}
                onChange={(key) => setFormData((prev) => ({ ...prev, licenseDocument: key || '' }))}
                folder="contractors/license"
                entityId={id}
                accept=".pdf,.doc,.docx,image/*"
                isDocument
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-[12px]">
          <button
            type="button"
            onClick={() => navigate('/contractors')}
            className="flex h-[48px] items-center gap-[8px] rounded-[8px] border border-[#e5e5e5] bg-white px-[24px] text-[14px] font-medium text-[#646464] transition-colors hover:border-[#646464] hover:text-[#181818]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex h-[48px] items-center gap-[8px] rounded-[8px] bg-[#055135] px-[24px] text-[14px] font-medium text-white transition-colors hover:bg-[#044128] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading && <Loader2 size={18} className="animate-spin" />}
            {isEditMode ? 'Update Contractor' : 'Create Contractor'}
          </button>
        </div>
      </form>
    </div>
  );
}
